import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { ArrowLeft, Plus, Eye, Trash2, Send, DollarSign, TrendingUp, BarChart3, AlertTriangle, Award, PieChart, ArrowUpRight, ArrowDownRight, Shield, FileDown, Pencil, Check, X as XIcon, Clock } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import NotificationBell from '@/components/NotificationBell';
import PdfViewerModal from '@/components/PdfViewerModal';
import ProfitabilityTablesDetailed from '@/components/ProfitabilityTablesDetailed';
import ApprovalTimeline from '@/components/ApprovalTimeline';

function ExecutiveSnapshot({ scenarios, aggregated, grossTotals, netFeesMargin, prePartnershipMargin, formatCurrency, formatPercent }) {
  // Derive insights from the data
  const totalGrossFees = grossTotals.total_fees;
  const totalNetFees = aggregated.netFees;
  const totalCollections = grossTotals.total_payments + grossTotals.total_fees;
  const totalCosts = aggregated.totalAdditionalCosts;
  const costToRevenueRatio = totalGrossFees > 0 ? (totalCosts / totalGrossFees * 100) : 0;
  const partnershipShare = totalGrossFees > 0 ? (aggregated.partnershipIncentive / totalGrossFees * 100) : 0;
  const merchantFeeRatio = totalGrossFees > 0 ? (grossTotals.merchant_fees / totalGrossFees * 100) : 0;

  // Per-service analysis
  const serviceMetrics = scenarios.map(s => {
    const t = s.profitability_results?.totals || {};
    const nf = s.profitability_results?.net_fees || {};
    return {
      name: s.service_name || s.name,
      municipality: s.scenario_info?.municipality,
      state: s.scenario_info?.state,
      grossFees: t.total_fees || 0,
      netFees: nf.net_fees || 0,
      impliedTR: t.implied_tr || 0,
      gpm: t.gpm || 0,
      npm: nf.net_fees_margin || 0,
      partnership: s.scenario_info?.partnership === 'Yes',
      readerType: s.scenario_info?.readerType,
      readersQty: s.scenario_info?.readersQty || 0,
    };
  });

  const highestMarginService = serviceMetrics.reduce((best, s) => s.npm > best.npm ? s : best, serviceMetrics[0]);
  const largestRevenueService = serviceMetrics.reduce((best, s) => s.grossFees > best.grossFees ? s : best, serviceMetrics[0]);
  const hasPartnership = serviceMetrics.some(s => s.partnership);
  const revenueConcentration = largestRevenueService && totalGrossFees > 0
    ? (largestRevenueService.grossFees / totalGrossFees * 100) : 0;

  // Build insights
  const insights = [];

  if (revenueConcentration > 80 && scenarios.length > 1) {
    insights.push({
      type: 'warning',
      icon: AlertTriangle,
      text: `${largestRevenueService.name} accounts for ${revenueConcentration.toFixed(0)}% of total fees — high concentration risk`
    });
  }

  if (highestMarginService && highestMarginService.npm > 30) {
    insights.push({
      type: 'positive',
      icon: Award,
      text: `${highestMarginService.name} leads with ${highestMarginService.npm.toFixed(1)}% net profit margin`
    });
  } else if (highestMarginService) {
    insights.push({
      type: 'neutral',
      icon: BarChart3,
      text: `Best net margin is ${highestMarginService.npm.toFixed(1)}% (${highestMarginService.name})`
    });
  }

  if (hasPartnership && partnershipShare > 5) {
    insights.push({
      type: 'info',
      icon: PieChart,
      text: `Partnership incentive consumes ${partnershipShare.toFixed(1)}% of gross fees (${formatCurrency(aggregated.partnershipIncentive)})`
    });
  }

  if (merchantFeeRatio > 60) {
    insights.push({
      type: 'warning',
      icon: ArrowDownRight,
      text: `Merchant fees are ${merchantFeeRatio.toFixed(1)}% of gross fees — above typical thresholds`
    });
  }

  if (grossTotals.gpm > 40) {
    insights.push({
      type: 'positive',
      icon: ArrowUpRight,
      text: `Strong gross profit margin at ${grossTotals.gpm.toFixed(1)}% across the proposal`
    });
  }

  const kpis = [
    { label: 'Annual Gross Fees', value: formatCurrency(totalGrossFees), sub: `from ${scenarios.length} service${scenarios.length !== 1 ? 's' : ''}`, color: 'bg-blue-600' },
    { label: 'Annual Net Fees', value: formatCurrency(totalNetFees), sub: `${netFeesMargin.toFixed(1)}% margin`, color: 'bg-emerald-600' },
    { label: 'Total Collections', value: formatCurrency(totalCollections), sub: 'payments + fees', color: 'bg-indigo-600' },
    { label: 'Avg Implied TR', value: formatPercent(grossTotals.implied_tr), sub: 'excl. ACH', color: 'bg-violet-600' },
    { label: 'Avg GPM', value: formatPercent(grossTotals.gpm), sub: 'gross profit margin', color: 'bg-amber-600' },
    { label: 'Total Costs', value: formatCurrency(totalCosts), sub: `${costToRevenueRatio.toFixed(1)}% of gross fees`, color: 'bg-red-600' },
  ];

  const insightColors = {
    positive: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    neutral: 'bg-slate-50 border-slate-200 text-slate-700',
  };

  const iconColors = {
    positive: 'text-emerald-600',
    warning: 'text-amber-600',
    info: 'text-blue-600',
    neutral: 'text-slate-500',
  };

  return (
    <div data-testid="executive-snapshot" className="space-y-5">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        {kpis.map((kpi, i) => (
          <div key={i} data-testid={`kpi-card-${i}`} className="card p-4 relative overflow-hidden">
            <div className={`absolute top-0 left-0 w-1 h-full ${kpi.color}`} />
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">{kpi.label}</p>
            <p className="text-xl font-bold text-slate-900 leading-tight">{kpi.value}</p>
            <p className="text-xs text-slate-500 mt-1">{kpi.sub}</p>
          </div>
        ))}
      </div>

      {/* Service Comparison + Insights */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        {/* Service-by-Service Comparison */}
        <div className="xl:col-span-2 card p-5">
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">Service Comparison</h3>
          <div className="overflow-x-auto">
            <table data-testid="service-comparison-table" className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-slate-200">
                  <th className="text-left py-2 pr-3 font-semibold text-slate-600">Service</th>
                  <th className="text-right py-2 px-3 font-semibold text-slate-600">Gross Fees</th>
                  <th className="text-right py-2 px-2 font-semibold text-slate-600 text-xs">% Gross</th>
                  <th className="text-right py-2 px-3 font-semibold text-slate-600">Net Fees</th>
                  <th className="text-right py-2 px-2 font-semibold text-slate-600 text-xs">% Net</th>
                  <th className="text-right py-2 px-3 font-semibold text-slate-600">TR</th>
                  <th className="text-right py-2 px-3 font-semibold text-slate-600">GPM</th>
                  <th className="text-right py-2 pl-3 font-semibold text-slate-600">NPM</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Calculate totals for percentage contribution
                  const totalGrossFees = serviceMetrics.reduce((sum, s) => sum + (s.grossFees || 0), 0);
                  const totalNetFees = serviceMetrics.reduce((sum, s) => sum + (s.netFees || 0), 0);
                  
                  return serviceMetrics.map((s, i) => {
                    const grossPct = totalGrossFees > 0 ? ((s.grossFees || 0) / totalGrossFees * 100) : 0;
                    const netPct = totalNetFees > 0 ? ((s.netFees || 0) / totalNetFees * 100) : 0;
                    
                    return (
                      <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-2.5 pr-3">
                          <p className="font-semibold text-slate-900">{s.name}</p>
                          <p className="text-xs text-slate-500">{s.municipality}{s.state ? `, ${s.state}` : ''}</p>
                        </td>
                        <td className="text-right py-2.5 px-3 font-medium">{formatCurrency(s.grossFees)}</td>
                        <td className="text-right py-2.5 px-2 text-blue-700 font-medium text-xs bg-blue-50/40">
                          {grossPct.toFixed(1)}%
                        </td>
                        <td className="text-right py-2.5 px-3 font-medium text-emerald-700">{formatCurrency(s.netFees)}</td>
                        <td className="text-right py-2.5 px-2 text-purple-700 font-medium text-xs bg-purple-50/40">
                          {netPct.toFixed(1)}%
                        </td>
                        <td className="text-right py-2.5 px-3">{s.impliedTR.toFixed(2)}%</td>
                        <td className="text-right py-2.5 px-3">{s.gpm.toFixed(1)}%</td>
                        <td className="text-right py-2.5 pl-3">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${s.npm >= 25 ? 'bg-emerald-100 text-emerald-800' : s.npm >= 15 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`}>
                            {s.npm.toFixed(1)}%
                          </span>
                        </td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
        </div>

        {/* Insights Panel */}
        <div className="card p-5">
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4 flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Key Insights
          </h3>
          <div className="space-y-3">
            {insights.length > 0 ? insights.map((insight, i) => {
              const Icon = insight.icon;
              return (
                <div key={i} data-testid={`insight-${i}`} className={`p-3 rounded-lg border text-sm ${insightColors[insight.type]}`}>
                  <div className="flex items-start gap-2">
                    <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${iconColors[insight.type]}`} />
                    <span>{insight.text}</span>
                  </div>
                </div>
              );
            }) : (
              <p className="text-sm text-slate-500">No notable insights for this proposal.</p>
            )}

            {/* Cost Breakdown */}
            <div className="mt-4 pt-4 border-t border-slate-200">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Cost Breakdown</p>
              <p className="text-[10px] text-slate-400 mb-3">% of gross fees</p>
              <div className="space-y-2 text-sm">
                {[
                  { label: 'Merchant Fees', value: grossTotals.merchant_fees, pct: merchantFeeRatio },
                  { label: 'Reader + SIM', value: aggregated.readerCosts + aggregated.simCosts, pct: totalGrossFees > 0 ? ((aggregated.readerCosts + aggregated.simCosts) / totalGrossFees * 100) : 0 },
                  { label: 'Sales Commissions', value: aggregated.salesCommissions, pct: totalGrossFees > 0 ? (aggregated.salesCommissions / totalGrossFees * 100) : 0 },
                  { label: 'Partnership', value: aggregated.partnershipIncentive, pct: partnershipShare },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-slate-600">{item.label}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-400 text-xs">{item.pct.toFixed(1)}%</span>
                      <span className="font-medium text-slate-800 w-20 text-right">{formatCurrency(item.value)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ProposalView() {
  const { proposalId } = useParams();
  const navigate = useNavigate();
  const [proposal, setProposal] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profitabilityTables, setProfitabilityTables] = useState(null);
  const [loadingProfTables, setLoadingProfTables] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ show: false, scenarioId: null, scenarioName: null });
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [pdfViewer, setPdfViewer] = useState({ show: false, url: null });
  const [submitModal, setSubmitModal] = useState({ show: false, loading: false });

  useEffect(() => {
    if (proposalId) {
      fetchProposalData();
    }
  }, [proposalId]);

  const fetchProposalData = async () => {
    try {
      setLoading(true);
      const [proposalRes, scenariosRes] = await Promise.all([
        api.get(`/collections/${proposalId}`),
        api.get(`/scenarios?collection_id=${proposalId}`),
      ]);
      setProposal(proposalRes.data);
      setNameInput(proposalRes.data.name || proposalRes.data.collection_name || '');
      const loadedScenarios = scenariosRes.data.scenarios || [];
      setScenarios(loadedScenarios);
      
      // Fetch profitability tables for the proposal
      if (loadedScenarios.length > 0) {
        fetchProfitabilityTables(loadedScenarios);
      }
    } catch (error) {
      console.error('Error fetching proposal data:', error);
      toast.error('Failed to load proposal');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchProfitabilityTables = async (scenarios) => {
    if (!scenarios || scenarios.length === 0) return;
    
    try {
      setLoadingProfTables(true);
      
      // Use the first scenario's data to calculate profitability tables
      const firstScenario = scenarios[0];
      const scenarioInfo = firstScenario.scenario_info || {};
      const volumeAssumptions = firstScenario.volume_assumptions || {};
      const proposedPricing = firstScenario.proposed_pricing || {};
      const transactionDistribution = firstScenario.transaction_distribution || [];
      
      // Transform transaction distribution
      const transformedDistribution = transactionDistribution.map(item => ({
        collectionAmount: item.collectionAmount,
        low: item.low,
        mid: item.mid,
        high: item.high,
        pct: item.currentPercent || item.pct || 0
      }));
      
      const calculationPayload = {
        annual_transactions: volumeAssumptions.totalTransactions || 10000,
        service_rate: 0.02,
        collection_amounts: transformedDistribution,
        payment_rates: proposedPricing.paymentRates || {},
        volume_mix: {
          ACH: (volumeAssumptions.achPercent || 0) / 100,
          CC: (volumeAssumptions.ccPercent || 0) / 100,
          AMEX: (volumeAssumptions.amexPercent || 0) / 100,
          DEBIT: (volumeAssumptions.debitPercent || 0) / 100
        },
        merchant_fees: {
          ACH: proposedPricing.paymentRates?.ACH?.merchantFee || 0,
          CC: proposedPricing.paymentRates?.CC?.merchantFee || 0,
          AMEX: proposedPricing.paymentRates?.AMEX?.merchantFee || 0,
          DEBIT: proposedPricing.paymentRates?.DEBIT?.merchantFee || 0
        },
        ach_fee_type: scenarioInfo.achFeeType || 'percentage'
      };
      
      const response = await api.post('/fee-table/profitability-tables', calculationPayload);
      if (response.data.success) {
        setProfitabilityTables(response.data.tables);
      }
    } catch (error) {
      console.error('Error fetching profitability tables:', error);
      // Don't show error toast, just fail silently
    } finally {
      setLoadingProfTables(false);
    }
  };

  const handleRemoveScenario = (scenarioId, scenarioName) => {
    setDeleteModal({ show: true, scenarioId, scenarioName });
  };

  const confirmRemoveScenario = async () => {
    const { scenarioId } = deleteModal;
    setDeleteModal({ show: false, scenarioId: null, scenarioName: null });
    try {
      await api.put(`/scenarios/${scenarioId}`, { collection_id: null });
      toast.success('Service removed from proposal');
      fetchProposalData();
    } catch (error) {
      console.error('Error removing scenario:', error);
      toast.error('Failed to remove service');
    }
  };

  const handleViewScenario = (scenarioId) => {
    navigate(`/calculator?scenario=${scenarioId}`);
  };

  // Derive the highest approval level from scenarios' approval_flags
  const getApprovalSummary = () => {
    let highest = 'standard';
    const reasons = [];
    for (const s of scenarios) {
      const flags = s.approval_flags || {};
      if (flags.requires_ceo_approval || flags.requires_cfo_approval) {
        highest = 'cfo_ceo';
      } else if (flags.requires_manager_approval && highest !== 'cfo_ceo') {
        highest = 'manager';
      }
      (flags.approval_reasons || []).forEach(r => {
        if (!reasons.includes(r)) reasons.push(r);
      });
    }
    const levelLabel = { standard: 'Standard Approver', manager: 'Manager (Bruner)', cfo_ceo: 'CFO / CEO' }[highest] || 'Approver';
    const levelColor = { standard: 'text-blue-700 bg-blue-50 border-blue-200', manager: 'text-orange-700 bg-orange-50 border-orange-200', cfo_ceo: 'text-red-700 bg-red-50 border-red-200' }[highest];
    return { highest, levelLabel, levelColor, reasons };
  };

  const handleSubmitProposal = () => {
    setSubmitModal({ show: true, loading: false });
  };

  const confirmSubmitProposal = async () => {
    try {
      setSubmitModal(prev => ({ ...prev, loading: true }));
      const response = await api.post(`/collections/${proposalId}/submit`);
      toast.success(response.data.message || 'Proposal submitted for approval');
      setSubmitModal({ show: false, loading: false });
      fetchProposalData();
    } catch (error) {
      console.error('Error submitting proposal:', error);
      const detail = error.response?.data?.detail || 'Failed to submit proposal';
      toast.error(detail);
      setSubmitModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleExportPDF = async () => {
    try {
      toast.info('Generating PDF...');
      const response = await api.get(`/collections/${proposalId}/export-pdf`, { responseType: 'arraybuffer' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      const filename = `Proposal_${proposal?.name || proposalId}.pdf`;
      setPdfViewer({ show: true, url: blobUrl, blob, filename });
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const closePdfViewer = () => {
    if (pdfViewer.url) URL.revokeObjectURL(pdfViewer.url);
    setPdfViewer({ show: false, url: null, blob: null, filename: '' });
  };

  const handleSaveName = async () => {
    const trimmed = nameInput.trim();
    if (!trimmed) {
      toast.error('Proposal name cannot be empty');
      return;
    }
    try {
      await api.put(`/collections/${proposalId}`, { name: trimmed });
      setProposal(prev => ({ ...prev, name: trimmed }));
      setEditingName(false);
      toast.success('Proposal name updated');
    } catch (error) {
      console.error('Error updating name:', error);
      toast.error('Failed to update proposal name');
    }
  };

  const handleCancelEditName = () => {
    setNameInput(proposal?.name || '');
    setEditingName(false);
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD',
      minimumFractionDigits: 2, maximumFractionDigits: 2
    }).format(value);
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return '-';
    return `${value.toFixed(2)}%`;
  };

  // Aggregate payment types across all scenarios
  const aggregated = scenarios.reduce((acc, scenario) => {
    const prof = scenario.profitability_results;
    if (!prof) return acc;
    const pt = prof.payment_types || {};
    const nf = prof.net_fees || {};

    // Sum payment type breakdowns
    ['CC', 'AMEX', 'DEBIT', 'ACH'].forEach(type => {
      if (pt[type]) {
        if (!acc.paymentTypes[type]) {
          acc.paymentTypes[type] = { total_payments: 0, total_fees: 0, merchant_fees: 0, net_revenue: 0 };
        }
        acc.paymentTypes[type].total_payments += pt[type].total_payments || 0;
        acc.paymentTypes[type].total_fees += pt[type].total_fees || 0;
        acc.paymentTypes[type].merchant_fees += pt[type].merchant_fees || 0;
        acc.paymentTypes[type].net_revenue += pt[type].net_revenue || 0;
      }
    });

    // Sum net fees components
    acc.readerCosts += nf.reader_costs?.total_cost || 0;
    acc.simCosts += nf.sim_costs?.total_cost || 0;
    acc.salesCommissions += nf.sales_commissions?.total_cost || 0;
    acc.totalCostsPrePartnership += nf.total_costs_pre_partnership || 0;
    acc.netFeesPrePartnership += nf.net_fees_pre_partnership || 0;
    acc.partnershipIncentive += nf.partnership_incentive?.total_cost || 0;
    acc.totalAdditionalCosts += nf.total_additional_costs || 0;
    acc.netFees += nf.net_fees || 0;

    return acc;
  }, {
    paymentTypes: {},
    readerCosts: 0, simCosts: 0, salesCommissions: 0,
    totalCostsPrePartnership: 0, netFeesPrePartnership: 0,
    partnershipIncentive: 0, totalAdditionalCosts: 0, netFees: 0
  });

  // Calculate gross totals from payment types
  const grossTotals = {
    total_payments: 0, total_fees: 0, merchant_fees: 0, net_revenue: 0
  };
  Object.values(aggregated.paymentTypes).forEach(pt => {
    grossTotals.total_payments += pt.total_payments;
    grossTotals.total_fees += pt.total_fees;
    grossTotals.merchant_fees += pt.merchant_fees;
    grossTotals.net_revenue += pt.net_revenue;
  });

  // Calculate percentage metrics for totals (excluding ACH)
  const nonAchPayments = (aggregated.paymentTypes.CC?.total_payments || 0) +
                         (aggregated.paymentTypes.AMEX?.total_payments || 0) +
                         (aggregated.paymentTypes.DEBIT?.total_payments || 0);
  const nonAchFees = (aggregated.paymentTypes.CC?.total_fees || 0) +
                     (aggregated.paymentTypes.AMEX?.total_fees || 0) +
                     (aggregated.paymentTypes.DEBIT?.total_fees || 0);
  const nonAchMerchantFees = (aggregated.paymentTypes.CC?.merchant_fees || 0) +
                             (aggregated.paymentTypes.AMEX?.merchant_fees || 0) +
                             (aggregated.paymentTypes.DEBIT?.merchant_fees || 0);
  const nonAchNetRevenue = (aggregated.paymentTypes.CC?.net_revenue || 0) +
                           (aggregated.paymentTypes.AMEX?.net_revenue || 0) +
                           (aggregated.paymentTypes.DEBIT?.net_revenue || 0);

  grossTotals.implied_tr = nonAchPayments > 0 ? (nonAchFees / nonAchPayments * 100) : 0;
  grossTotals.implied_mf = nonAchFees > 0 ? (nonAchMerchantFees / nonAchFees * 100) : 0;
  grossTotals.gpm = nonAchFees > 0 ? (nonAchNetRevenue / nonAchFees * 100) : 0;

  const netFeesMargin = grossTotals.total_fees > 0 ? (aggregated.netFees / grossTotals.total_fees * 100) : 0;
  const prePartnershipMargin = grossTotals.total_fees > 0 ? (aggregated.netFeesPrePartnership / grossTotals.total_fees * 100) : 0;

  // Per-type implied TR calculation
  const calcImpliedTR = (pt) => pt.total_payments > 0 ? (pt.total_fees / pt.total_payments * 100) : 0;
  const calcImpliedMF = (pt) => pt.total_fees > 0 ? (pt.merchant_fees / pt.total_fees * 100) : 0;
  const calcGPM = (pt) => pt.total_fees > 0 ? (pt.net_revenue / pt.total_fees * 100) : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="text-slate-600 mt-4">Loading proposal...</p>
        </div>
      </div>
    );
  }

  if (!proposal) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-600">Proposal not found</p>
          <button onClick={() => navigate('/proposals')} className="btn-primary mt-4">
            Back to Proposals
          </button>
        </div>
      </div>
    );
  }

  const paymentTypeRows = [
    { key: 'CC', label: 'Non-Amex Payments' },
    { key: 'AMEX', label: 'AMEX Payments' },
    { key: 'DEBIT', label: 'Debit Card Payments' },
    { key: 'ACH', label: 'ACH Payments' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/proposals')}
                data-testid="back-to-proposals-btn"
                className="flex items-center gap-2 px-4 py-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-700 font-medium"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Proposals</span>
              </button>
              <div className="border-l border-slate-300 h-8"></div>
              <div>
                {editingName ? (
                  <div className="flex items-center gap-2">
                    <input
                      data-testid="edit-proposal-name-input"
                      type="text"
                      value={nameInput}
                      onChange={(e) => setNameInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') handleCancelEditName(); }}
                      className="text-2xl font-bold text-slate-900 border-b-2 border-indigo-500 bg-transparent outline-none px-1 py-0"
                      autoFocus
                    />
                    <button data-testid="save-name-btn" onClick={handleSaveName} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded">
                      <Check className="w-5 h-5" />
                    </button>
                    <button data-testid="cancel-name-btn" onClick={handleCancelEditName} className="p-1 text-slate-400 hover:bg-slate-100 rounded">
                      <XIcon className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 group">
                    <h1 className="text-2xl font-bold text-slate-900">{proposal.name}</h1>
                    {proposal.status === 'draft' && (
                      <button
                        data-testid="edit-proposal-name-btn"
                        onClick={() => setEditingName(true)}
                        className="p-1 text-slate-400 opacity-0 group-hover:opacity-100 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all"
                        title="Edit proposal name"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
                <p className="text-sm text-slate-600">
                  {scenarios.length} service{scenarios.length !== 1 ? 's' : ''} &bull; 
                  Created {new Date(proposal.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Status Badge */}
              {proposal.status && proposal.status !== 'draft' && (
                <span data-testid="proposal-status-badge" className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                  proposal.status === 'approved' || proposal.status === 'fully_approved' ? 'bg-emerald-100 text-emerald-700' :
                  proposal.status === 'rejected' || proposal.status === 'fully_denied' ? 'bg-red-100 text-red-700' :
                  'bg-amber-100 text-amber-700'
                }`}>
                  {proposal.status === 'pending_approval' ? 'Pending Approval' :
                   proposal.status === 'pending_cfo_approval' ? 'Pending CFO/CEO' :
                   proposal.status === 'submitted' ? 'Submitted' :
                   proposal.status === 'approved' || proposal.status === 'fully_approved' ? 'Approved' :
                   proposal.status === 'rejected' || proposal.status === 'fully_denied' ? 'Rejected' :
                   proposal.status}
                </span>
              )}
              <button
                onClick={handleExportPDF}
                data-testid="export-pdf-btn"
                className="btn-secondary flex items-center gap-2"
                disabled={scenarios.length === 0}
              >
                <FileDown className="w-4 h-4" />
                Export PDF
              </button>
              {(!proposal.status || proposal.status === 'draft') && (
                <>
                  <button
                    onClick={() => navigate(`/calculator?proposal=${proposalId}`)}
                    data-testid="add-service-btn"
                    className="btn-secondary flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add Service
                  </button>
                  <button
                    onClick={handleSubmitProposal}
                    data-testid="submit-proposal-btn"
                    className="btn-primary flex items-center gap-2"
                    disabled={scenarios.length === 0}
                  >
                    <Send className="w-4 h-4" />
                    Submit for Approval
                  </button>
                </>
              )}
              <NotificationBell />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">

        {/* Executive Snapshot */}
        {scenarios.length > 0 && <ExecutiveSnapshot scenarios={scenarios} aggregated={aggregated} grossTotals={grossTotals} netFeesMargin={netFeesMargin} prePartnershipMargin={prePartnershipMargin} formatCurrency={formatCurrency} formatPercent={formatPercent} />}

        {/* Approval Timeline - Show if proposal has been submitted */}
        {(proposal.status !== 'draft' || proposal.submitted_at) && (
          <div className="mb-8 bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
            <ApprovalTimeline proposal={proposal} />
          </div>
        )}

        {/* Profitability Tables */}
        {scenarios.length > 0 && (scenarios[0]?.calculation_results || scenarios[0]?.profitability_results) && (
          <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
            <ProfitabilityTablesDetailed 
              profitabilityResult={scenarios[0].profitability_results || scenarios[0].calculation_results}
              scenarios={scenarios}
              loading={loading} 
            />
          </div>
        )}

        {/* Aggregated Profitability Tables */}
        {scenarios.length > 0 && (
          <div data-testid="proposal-summary-tables" className="space-y-6">
            {/* Header */}
            <div className="card p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-l-blue-500">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                    <TrendingUp className="w-7 h-7 text-blue-600" />
                    Proposal Summary — Combined Profitability
                  </h2>
                  <p className="text-slate-600 mt-1">
                    Totals across {scenarios.length} service{scenarios.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            </div>

            {/* Two-Panel Layout: Gross Fees & Net Fees */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

              {/* LEFT PANEL: Gross Fees Calculation */}
              <div className="card p-6">
                <div className="bg-blue-600 text-white px-4 py-3 rounded-t-lg -mx-6 -mt-6 mb-6">
                  <h3 className="text-lg font-bold">Gross Fees Calculation</h3>
                </div>

                <div className="overflow-x-auto">
                  <table data-testid="gross-fees-table" className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-slate-300">
                        <th className="text-left p-2 font-semibold">Payment Type</th>
                        <th className="text-right p-2 font-semibold">Payments</th>
                        <th className="text-right p-2 font-semibold">Fees</th>
                        <th className="text-right p-2 font-semibold">Implied TR (%)</th>
                        <th className="text-right p-2 font-semibold">Merchant Fees</th>
                        <th className="text-right p-2 font-semibold">Net Revenue</th>
                        <th className="text-right p-2 font-semibold">GPM</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentTypeRows.map(({ key, label }) => {
                        const pt = aggregated.paymentTypes[key];
                        if (!pt || (key === 'DEBIT' && pt.total_payments === 0)) return null;
                        return (
                          <tr key={key} className="bg-blue-50 hover:bg-blue-100">
                            <td className="p-2 font-medium text-blue-900">{label}</td>
                            <td className="text-right p-2">{formatCurrency(pt.total_payments)}</td>
                            <td className="text-right p-2 font-semibold">{formatCurrency(pt.total_fees)}</td>
                            <td className="text-right p-2">{formatPercent(calcImpliedTR(pt))}</td>
                            <td className="text-right p-2">{formatCurrency(pt.merchant_fees)}</td>
                            <td className="text-right p-2 font-semibold">{formatCurrency(pt.net_revenue)}</td>
                            <td className="text-right p-2 font-semibold text-green-700">{formatPercent(calcGPM(pt))}</td>
                          </tr>
                        );
                      })}

                      {/* Totals Row */}
                      <tr className="bg-green-100 font-bold border-t-2 border-green-600">
                        <td className="p-2 text-green-900">Totals</td>
                        <td className="text-right p-2">{formatCurrency(grossTotals.total_payments)}</td>
                        <td className="text-right p-2 text-green-900">{formatCurrency(grossTotals.total_fees)}</td>
                        <td className="text-right p-2">{formatPercent(grossTotals.implied_tr)}</td>
                        <td className="text-right p-2">{formatCurrency(grossTotals.merchant_fees)}</td>
                        <td className="text-right p-2 text-green-900">{formatCurrency(grossTotals.net_revenue)}</td>
                        <td className="text-right p-2 text-green-900">{formatPercent(grossTotals.gpm)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* RIGHT PANEL: Net Fees Calculation */}
              <div className="card p-6">
                <div className="bg-red-600 text-white px-4 py-3 rounded-t-lg -mx-6 -mt-6 mb-6">
                  <h3 className="text-lg font-bold">Net Fees Calculation</h3>
                </div>

                <div className="space-y-4">
                  {/* Reader Costs */}
                  {aggregated.readerCosts > 0 && (
                    <div className="bg-red-50 p-4 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-slate-600 font-medium">Reader Costs</span>
                        <span className="font-semibold text-slate-900">(combined)</span>
                      </div>
                      <div className="mt-2 pt-2 border-t border-red-200">
                        <div className="flex justify-between font-bold text-red-900">
                          <span>Total Reader Costs</span>
                          <span>{formatCurrency(aggregated.readerCosts)}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* SIM Costs */}
                  {aggregated.simCosts > 0 && (
                    <div className="bg-red-50 p-4 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-slate-600 font-medium">SIM Card Costs</span>
                        <span className="font-semibold text-slate-900">(combined annual)</span>
                      </div>
                      <div className="mt-2 pt-2 border-t border-red-200">
                        <div className="flex justify-between font-bold text-red-900">
                          <span>Total SIM Costs</span>
                          <span>{formatCurrency(aggregated.simCosts)}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Sales Commissions */}
                  {aggregated.salesCommissions > 0 && (
                    <div className="bg-red-50 p-4 rounded-lg">
                      <div className="flex justify-between font-bold text-red-900">
                        <span>Sales Commissions</span>
                        <span>{formatCurrency(aggregated.salesCommissions)}</span>
                      </div>
                    </div>
                  )}

                  {/* Total Costs Pre-Partnership */}
                  <div className="bg-amber-50 p-4 rounded-lg border-2 border-amber-400">
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-amber-900">Total Costs Pre-Partnership</span>
                      <span className="text-xl font-bold text-amber-900">
                        {formatCurrency(aggregated.totalCostsPrePartnership)}
                      </span>
                    </div>
                  </div>

                  {/* Net Fees Pre-Partnership */}
                  <div className="bg-green-100 p-4 rounded-lg border-2 border-green-600">
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-green-900">Net Fees Pre-Partnership</span>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-green-900">{formatCurrency(aggregated.netFeesPrePartnership)}</p>
                        <p className="text-sm text-green-700">{formatPercent(prePartnershipMargin)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Partnership Incentive */}
                  {aggregated.partnershipIncentive > 0 && (
                    <div className="bg-red-50 p-4 rounded-lg">
                      <div className="flex justify-between font-bold text-red-900">
                        <span>Partnership Incentive</span>
                        <span>{formatCurrency(aggregated.partnershipIncentive)}</span>
                      </div>
                    </div>
                  )}

                  {/* Total Additional Costs */}
                  <div className="bg-red-100 p-4 rounded-lg border border-red-400">
                    <div className="flex justify-between font-bold text-red-900">
                      <span>Total Additional Costs</span>
                      <span className="text-lg">{formatCurrency(aggregated.totalAdditionalCosts)}</span>
                    </div>
                  </div>

                  {/* Final Net Fees */}
                  <div className="bg-green-100 p-4 rounded-lg border-2 border-green-600">
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-green-900">Net Fees</span>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-green-900">{formatCurrency(aggregated.netFees)}</p>
                        <p className="text-sm text-green-700">{formatPercent(netFeesMargin)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Individual Services */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-slate-900">Services in Proposal</h2>
            <span className="text-sm text-slate-600">{scenarios.length} total</span>
          </div>

          {scenarios.length === 0 ? (
            <div className="text-center py-12">
              <DollarSign className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 mb-4">No services added yet</p>
              <button
                onClick={() => navigate(`/calculator?proposal=${proposalId}`)}
                className="btn-primary"
              >
                <Plus className="w-4 h-4 mr-2 inline" />
                Add First Service
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {scenarios.map((scenario, index) => {
                const prof = scenario.profitability_results;
                const totals = prof?.totals || {};
                const netFees = prof?.net_fees || {};
                const contributionMargin = netFees.net_fees_margin || 0;

                return (
                  <div key={scenario.id} className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-2xl font-bold text-indigo-600">#{index + 1}</span>
                          <div>
                            <h3 className="text-lg font-semibold text-slate-900">{scenario.name}</h3>
                            <p className="text-sm text-slate-600">{scenario.service_name}</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mt-4 text-sm">
                          <div>
                            <p className="text-slate-500">Total Fees</p>
                            <p className="font-semibold text-slate-900">{formatCurrency(totals.total_fees)}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Net Revenue</p>
                            <p className="font-semibold text-green-600">{formatCurrency(totals.net_revenue)}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Net Fees</p>
                            <p className="font-semibold text-emerald-600">{formatCurrency(netFees.net_fees)}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Implied TR</p>
                            <p className="font-semibold text-slate-900">{totals.implied_tr?.toFixed(2)}%</p>
                          </div>
                          <div>
                            <p className="text-slate-500">GPM</p>
                            <p className="font-semibold text-slate-900">{totals.gpm?.toFixed(2)}%</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Contribution Margin %</p>
                            <p className="font-semibold text-indigo-600">{contributionMargin?.toFixed(2)}%</p>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => handleViewScenario(scenario.id)}
                          data-testid={`view-scenario-${index}`}
                          className="btn-secondary flex items-center gap-2"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </button>
                        <button
                          onClick={() => handleRemoveScenario(scenario.id, scenario.name)}
                          data-testid={`remove-scenario-${index}`}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Remove from proposal"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div data-testid="delete-confirm-modal" className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <h3 className="text-lg font-bold text-slate-900 mb-2">Remove Service</h3>
            <p className="text-slate-600 mb-6">
              Are you sure you want to remove <span className="font-semibold">"{deleteModal.scenarioName}"</span> from this proposal?
            </p>
            <div className="flex justify-end gap-3">
              <button
                data-testid="cancel-delete-btn"
                onClick={() => setDeleteModal({ show: false, scenarioId: null, scenarioName: null })}
                className="px-4 py-2 text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                data-testid="confirm-delete-btn"
                onClick={confirmRemoveScenario}
                className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg font-medium transition-colors"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Viewer Modal */}
      {pdfViewer.show && (
        <PdfViewerModal
          url={pdfViewer.url}
          name={proposal?.name || 'Proposal'}
          blob={pdfViewer.blob}
          filename={pdfViewer.filename}
          onClose={closePdfViewer}
        />
      )}

      {/* Submit for Approval Confirmation Modal */}
      {submitModal.show && (() => {
        const { highest, levelLabel, levelColor, reasons } = getApprovalSummary();
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div data-testid="submit-confirm-modal" className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-2 rounded-lg ${highest === 'cfo_ceo' ? 'bg-red-50' : highest === 'manager' ? 'bg-orange-50' : 'bg-blue-50'}`}>
                  {highest === 'cfo_ceo' ? <AlertTriangle className="w-5 h-5 text-red-600" /> :
                   highest === 'manager' ? <Shield className="w-5 h-5 text-orange-600" /> :
                   <Send className="w-5 h-5 text-blue-600" />}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Submit for Approval</h3>
                  <p className="text-sm text-slate-500">{scenarios.length} service{scenarios.length !== 1 ? 's' : ''} in this proposal</p>
                </div>
              </div>

              {/* Approval Level Badge */}
              <div data-testid="approval-level-badge" className={`border rounded-lg p-3 mb-4 ${levelColor}`}>
                <p className="text-sm font-semibold">Required Approval Level: {levelLabel}</p>
              </div>

              {/* Approval Reasons */}
              {reasons.length > 0 && (
                <div data-testid="approval-reasons" className="mb-4">
                  <p className="text-sm font-medium text-slate-700 mb-2">Threshold Flags:</p>
                  <ul className="space-y-1.5">
                    {reasons.map((reason, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                        <span>{reason}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {reasons.length === 0 && (
                <p className="text-sm text-slate-500 mb-4">
                  This proposal meets all standard thresholds and will be routed to the {levelLabel} queue.
                </p>
              )}

              <div className="flex justify-end gap-3 pt-2 border-t border-slate-100">
                <button
                  data-testid="cancel-submit-btn"
                  onClick={() => setSubmitModal({ show: false, loading: false })}
                  disabled={submitModal.loading}
                  className="px-4 py-2 text-sm text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  data-testid="confirm-submit-btn"
                  onClick={confirmSubmitProposal}
                  disabled={submitModal.loading}
                  className="px-4 py-2 text-sm text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {submitModal.loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Confirm Submission
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
